import { create } from "zustand";
import { persist } from "zustand/middleware";
import { blockUserApi, getBlockedUsersApi, unblockUserApi } from "../api/user/blocksApi";
import { useAuthStore } from "./useAuthStore";

/**
 * Users the person using this device has blocked.
 *
 * App Review Guideline 1.2 requires a way to block abusive users, and blocking
 * has to take effect the instant it is tapped — so the list is applied locally
 * first. Signed in, every change also goes to the server (B11), which stops
 * the blocked user's messages, notifications and listings for this account on
 * every device; `syncBlockedUsers` pulls the account's list after login.
 *
 * Blocked users are hidden rather than deleted: their listings drop out of the
 * lists and their comments are replaced by a placeholder the reader can undo,
 * so nobody is left staring at a gap they cannot explain.
 */
export interface BlockedUser {
  id: number;
  name: string;
  blockedAt: string;
}

interface BlockState {
  blocked: BlockedUser[];
  /** Ids the server confirmed — missing from its list later means «unblocked on another device». */
  onServer: number[];
  blockUser: (user: { id: number; name?: string | null }) => void;
  unblockUser: (id: number) => void;
}

const signedIn = () => useAuthStore.getState().isAuthenticated;

export const useBlockStore = create<BlockState>()(
  persist(
    (set) => ({
      blocked: [],
      onServer: [],

      blockUser: ({ id, name }) => {
        if (signedIn()) {
          void blockUserApi(id).then(
            () => set((s) => ({ onServer: s.onServer.includes(id) ? s.onServer : [...s.onServer, id] })),
            () => undefined,
          );
        }
        set((state) =>
          state.blocked.some((u) => u.id === id)
            ? state
            : {
                blocked: [
                  ...state.blocked,
                  {
                    id,
                    name: name?.trim() || "—",
                    blockedAt: new Date().toISOString(),
                  },
                ],
              },
        );
      },

      unblockUser: (id) => {
        if (signedIn()) void unblockUserApi(id).catch(() => undefined);
        set((state) => ({
          blocked: state.blocked.filter((u) => u.id !== id),
          onServer: state.onServer.filter((x) => x !== id),
        }));
      },
    }),
    { name: "blockedUsers" },
  ),
);

/**
 * After login: blocks made on this device while signed out (or before B11)
 * go up to the server, then the list becomes the account's list. A server
 * without B11 leaves the device list as it is.
 */
export async function syncBlockedUsers(): Promise<void> {
  let server;
  try {
    server = await getBlockedUsersApi();
  } catch {
    return;
  }
  const { blocked, onServer } = useBlockStore.getState();
  const serverIds = new Set(server.map((u) => u.id));
  const confirmed = new Set(onServer);
  const upload = blocked.filter((u) => !serverIds.has(u.id) && !confirmed.has(u.id));
  const uploaded = await Promise.all(upload.map((u) => blockUserApi(u.id).then(() => u, () => null)));
  const merged: BlockedUser[] = [
    ...server.map((u) => ({
      id: u.id,
      name: u.name?.trim() || blocked.find((b) => b.id === u.id)?.name || "—",
      blockedAt: u.blockedAt,
    })),
    ...uploaded.filter((u): u is BlockedUser => u !== null),
  ];
  useBlockStore.setState({ blocked: merged, onServer: merged.map((u) => u.id) });
}

/**
 * Subscribes to the blocked list rather than reading it once, so a component
 * re-renders the moment the user blocks or unblocks someone.
 */
export const useIsBlocked = (id?: number | null): boolean =>
  useBlockStore((s) =>
    id == null ? false : s.blocked.some((u) => u.id === id),
  );
